/**
 * Sales tax by province, for the order-total preview.
 *
 * MUST match `web/src/modules/estimates/tax.ts`, which is the authority — the server
 * recomputes tax when the order is created, so any disagreement here shows the
 * operator a total that differs from what is actually stored and charged.
 *
 * Rates confirmed by the client 2026-08-17; tax follows the shipping destination.
 * Quebec is GST-only per their instruction (QST is administered separately and only
 * charged once registered for it).
 */
const HST_RATES: Record<string, number> = {
  ON: 0.13,
  NS: 0.14, // reduced from 15% on 1 Apr 2025
  NB: 0.15,
  NL: 0.15,
  PE: 0.15,
};

const GST_ONLY = new Set(['BC', 'AB', 'SK', 'MB', 'QC', 'NT', 'YT', 'NU']);

export const DEFAULT_TAX_RATE = 0.13;

/** Rate for a province code, or the Ontario default when it can't be determined. */
export function taxRateForProvince(province?: string | null): number {
  const code = (province || '').trim().toUpperCase();
  if (HST_RATES[code] !== undefined) return HST_RATES[code];
  if (GST_ONLY.has(code)) return 0.05;
  return DEFAULT_TAX_RATE;
}

/** True when the province is recognised, so the UI can say the rate is a fallback. */
export function isKnownProvince(province?: string | null): boolean {
  const code = (province || '').trim().toUpperCase();
  return HST_RATES[code] !== undefined || GST_ONLY.has(code);
}

export function taxLabelForProvince(province?: string | null): string {
  const code = (province || '').trim().toUpperCase();
  if (HST_RATES[code] !== undefined) return `${(HST_RATES[code] * 100).toFixed(0)}% HST (${code})`;
  if (GST_ONLY.has(code)) return `5% GST (${code})`;
  return '13% HST';
}
